import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Chat, ChatEntity, ChatFile, Shortcut, Stream, StreamSubject } from '@iese-chatbot/common-utils';
import { delay, map, Observable, of } from 'rxjs';
import { CHAT_DATA_ACCESS_CONFIG, ChatDataAccessConfig } from '../config';
import { mockChats } from '../mock/chat.mock';
import { XHR_SERVICE } from '../providers/xhr-service.token';
import { XhrService } from './xhr.service';

@Injectable({ providedIn: 'root' })
export class ChatDataService {
  private readonly apiUrl: string;
  private readonly mock: boolean;

  constructor(
    private httpClient: HttpClient,
    @Inject(XHR_SERVICE) private xhrService: XhrService,
    @Inject(CHAT_DATA_ACCESS_CONFIG) config: ChatDataAccessConfig,
  ) {
    this.apiUrl = config.apiUrl;
    this.mock = config.mock;
  }

  public fetchChatData$(): Observable<Chat[]> {
    if (this.mock) {
      return of(mockChats);
    } else {
      return this.httpClient
        .get<ChatEntity[]>(`${this.apiUrl}/entities/user`)
        .pipe(map((result) => result.map((entity) => entity.chat)));
    }
  }

  public fetchTitle$(chat: Chat): Observable<string> {
    if (this.mock) {
      return of('mocked chat title');
    } else {
      return this.httpClient
        .post<Stream>(`${this.apiUrl}/event/title`, chat)
        .pipe(map((result) => result.choices[0].message?.content.replace(/"/g, '') ?? ''));
    }
  }

  public saveChatData$(chat: Chat): Observable<ChatEntity> {
    if (this.mock) {
      return of({ id: chat.id, chat });
    } else {
      const chatEntity: ChatEntity = { id: chat.id, chat };
      return this.httpClient.post<ChatEntity>(`${this.apiUrl}/entities/save`, chatEntity);
    }
  }

  public deleteChat$(id: string): Observable<boolean> {
    if (this.mock) {
      return of(true);
    } else {
      return this.httpClient.delete<boolean>(`${this.apiUrl}/entities/${id}`);
    }
  }

  public sendMessage$(chat: Chat, files: File[]): Observable<StreamSubject> {
    return this.xhrService.xhrConnection(`${this.apiUrl}/event/stream`, chat, files);
  }

  public sendShortcut$(chat: Chat, shortcut: Shortcut, files: File[]): Observable<StreamSubject> {
    return this.xhrService.xhrConnection(`${this.apiUrl}/event/shortcut/${shortcut.type}`, chat, files);
  }

  public stopMessage(): void {
    this.xhrService.stopStream();
  }

  public deleteFile(chat: Chat, file: ChatFile): Observable<boolean> {
    if (this.mock) {
      return of(true).pipe(delay(1500));
    } else {
      return this.httpClient.delete<boolean>(`${this.apiUrl}/event/${chat.id}/file/${file.name}`);
    }
  }
}
